import React, { useState, useMemo } from "react";
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BWITextField from "../shared/BWITextField";
import { useSession } from "../../context/SessionContext";

interface SystemAccessSelectorProps {
  selectedSystems: string[];
  onSystemSelect: (systemCode: string) => void;
  disabled?: boolean;
}

const SystemAccessSelector: React.FC<SystemAccessSelectorProps> = ({
  selectedSystems,
  onSystemSelect,
  disabled
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { systems } = useSession()

  const getSystemCode = (system: { systemCode?: string | null }) => system.systemCode?.trim() || "";
  const getSystemName = (system: { commonName?: string | null; systemCode?: string | null }) =>
    system.commonName?.trim() || getSystemCode(system) || "Unnamed system";

  // Fuzzy search filter logic for both systemCode & commonName
  const filteredSystems = useMemo(() => {
    const normalizedQuery = searchQuery.toLowerCase();
    const filtered = systems?.filter(
      (system) => {
        const systemCode = getSystemCode(system).toLowerCase();
        const commonName = getSystemName(system).toLowerCase();

        return systemCode.includes(normalizedQuery) || commonName.includes(normalizedQuery);
      }
    ) || [];

    return filtered.sort((a, b) => getSystemName(a).localeCompare(getSystemName(b)));
  }, [searchQuery, systems]);


  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <BWITextField
        label="Search System Access"
        fullWidth
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2, flexShrink: 0 }}
        startIcon={<SearchIcon />}
        size={isMobile ? 'small' : 'medium'}
        data-cy="system-access-search-input"
      />

      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto'
      }}>
        <FormGroup>
          {filteredSystems.map((system) => (
            <FormControlLabel
              key={getSystemCode(system) || getSystemName(system)}
              control={
                <Checkbox
                  checked={selectedSystems.includes(getSystemCode(system))}
                  onChange={() => {
                    const systemCode = getSystemCode(system);
                    if (!disabled && systemCode) {
                      onSystemSelect(systemCode);
                    }
                  }}
                  disabled={disabled || !getSystemCode(system)}
                  data-cy={`system-access-checkbox-${getSystemCode(system)}`}
                  sx={{
                    "&.Mui-checked": {
                      color: "#d32f2f",
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  <strong>{getSystemName(system)}</strong>
                  {getSystemCode(system) ? ` (${getSystemCode(system)})` : ""}
                </Typography>
              }
            />
          ))}
        </FormGroup>
      </Box>
    </Paper>
  );
};

export default SystemAccessSelector;
